import React from 'react';
import { Card, Badge, Button, Typography, Flex, theme } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import './renderingLocationCard.css';

type Availability = 'current' | 'locked' | 'available';

type RenderingLocationCardProps = {
  icon: string;
  title: string;
  description: string;
  onSelect: () => void;
  onUpgrade: () => void;
  availability: Availability;
};

function RenderingLocationCard({
  icon,
  title,
  description,
  onSelect,
  onUpgrade,
  availability,
}: RenderingLocationCardProps) {
  return (
    <CardWithBadge badgeText={availability === 'current' ? 'Current' : undefined}>
      <CardContent
        icon={icon}
        title={title}
        description={description}
        availability={availability}
        onSelect={onSelect}
        onUpgrade={onUpgrade}
      />
    </CardWithBadge>
  );
}

function CardWithBadge({ children, badgeText }: React.PropsWithChildren<{ badgeText?: string }>) {
  return badgeText ? (
    <Badge.Ribbon text={badgeText} color="#C6C6C6" rootClassName="rendering-location-card-ribbon">
      <Card style={{ height: '100%' }} styles={{ body: { padding: 16, height: '100%' } }}>
        {children}
      </Card>
    </Badge.Ribbon>
  ) : (
    <Card style={{ border: '1px solid #E3E3E3', height: '100%' }} styles={{ body: { padding: 16, height: '100%' } }}>
      {children}
    </Card>
  );
}

type CardContentProps = {
  icon: string;
  title: string;
  description: string;
  availability: Availability;
  onSelect: () => void;
  onUpgrade: () => void;
};

function CardContent({ icon, title, description, availability, onSelect, onUpgrade }: CardContentProps) {
  return (
    <Flex vertical align="center" gap="middle" justify="space-between" flex={1} style={{ height: '100%' }}>
      <Flex vertical align="center" gap="middle" justify="start" flex={1}>
        <div>
          {availability === 'locked' ? (
            <LockOutlined className="fa-3x fe" />
          ) : (
            <i className={`fa-3x fe ${icon}`} style={{ lineHeight: '42px' }}></i>
          )}
        </div>
        <Flex vertical gap={6} style={{ textAlign: 'center' }}>
          <Typography.Text strong>{title}</Typography.Text>
          <Typography.Paragraph style={{ margin: 0 }}>{description}</Typography.Paragraph>
        </Flex>
      </Flex>

      {availability === 'current' ? (
        <ButtonCurrent>{'Current'}</ButtonCurrent>
      ) : availability === 'locked' ? (
        <ButtonLocked onClick={onUpgrade}>{'Upgrade to Growth'}</ButtonLocked>
      ) : (
        <ButtonUnlocked onClick={onSelect}>{'Select'}</ButtonUnlocked>
      )}
    </Flex>
  );
}

function ButtonCurrent({ children }: React.PropsWithChildren) {
  return (
    <Button type="primary" variant="filled" block disabled>
      {children}
    </Button>
  );
}

function ButtonLocked({ children, onClick }: React.PropsWithChildren<{ onClick: () => void }>) {
  const { token } = theme.useToken();
  return (
    <Button
      type="primary"
      variant="filled"
      block
      style={{ color: token.colorInfo, backgroundColor: token.colorInfoBg, boxShadow: '0px 0px 0px 0px' }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function ButtonUnlocked({ children, onClick }: React.PropsWithChildren<{ onClick: () => void }>) {
  return (
    <Button type="primary" variant="filled" block onClick={onClick}>
      {children}
    </Button>
  );
}

export default RenderingLocationCard;
